// src/components/Inquiry/items/inquiryLine.ts
//
// One shape for an inquiry line, whichever end of its life it is at.
//
// A line was rendered by two unrelated table stacks before this: a read-only
// one under pages/outsourcing_management/InquiryTable driven by
// InquiryItemType, and an editable one under components/Inquiry driven by
// OrderItemType | CartItemType plus a CreateInquiryItemType selection. They
// disagreed about where the quantity lives, what a line with no product means
// and what a price defaults to - which is how "undefinedpcs", the dropped
// quantity on re-tick and the zeroed custom price all reached production.
//
// The two adapters below are the whole difference between the inputs. Past
// them there is one row model and one table.
import { InquiryItemType } from "types/InquiryTypes";
import { CartItemType } from "types/CartTypes";
import { OrderItemType } from "types/OrderTypes";
import { ProductVMType } from "types/products/ProductCommandsType";

export const EMPTY = "-";

export type InquiryLine = {
  /** React key: the inquiry line's id when saved, the source item's when not. */
  key: number;
  productId: number;
  /**
   * `null` only on lines written before EE-253, when converting a Requisition
   * left the line without a PRODUCTS row and put the spec in `comment`.
   */
  product: ProductVMType | null;
  quantity: number | null;
  /** The spec on a legacy converted line. */
  comment?: string | null;
  orderItemId?: number | null;
  quoteItemId?: number | null;
  /** The saved line itself, when there is one - the goods-in cells PATCH it. */
  item?: InquiryItemType;
};

/**
 * A saved line. InquiryItemVM always serialises `quantity`, and fills it from
 * the order / quote line for legacy lines that have none of their own - the
 * machined row used to read it off those lines only, so a line raised from
 * neither rendered the string "undefinedpcs".
 */
export const lineFromInquiryItem = (item: InquiryItemType): InquiryLine => ({
  key: item.id,
  // A legacy converted line has no product at all; -1 marks it, and only the
  // editable table matches lines by product id - it never lists these.
  productId: item.product?.id ?? -1,
  product: item.product ?? null,
  quantity: item.quantity ?? null,
  comment: item.comment,
  orderItemId: item.orderItemId,
  quoteItemId: item.quoteItemId,
  item,
});

/**
 * A line being picked off an order or a quote. `orderItemId` / `quoteItemId`
 * are what the payload needs to point the inquiry back at the source line.
 */
export const lineFromOrderItem = (
  item: OrderItemType | CartItemType,
  type: "order" | "cart"
): InquiryLine => ({
  key: item.id,
  productId: item.product.id,
  product: item.product,
  quantity: item.quantity,
  orderItemId: type === "order" ? item.id : null,
  quoteItemId: type === "cart" ? item.id : null,
});

export const formatLineQuantity = (line: InquiryLine): string =>
  line.quantity === null || line.quantity === undefined
    ? EMPTY
    : `${line.quantity} pcs`;
